import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import ToolTip from '../../ToolTipIcon/ToolTip';
import PaginationPanel from '../../../containers/Pagination/Pagination';
import PaginationInfo from '../../../containers/PaginationInfo/PaginationInfo';
import CustomFile from '../CustomFile/CustomFile';
import '../../../containers/Pages/Edd/Edd.css';
import {
  capitalize,
  getImportTooltip,
  insertSpaceAt46th,
} from '../../../containers/Pages/Edd/EddUtils';
import Spinner from '../../Spinner/Spinner';

export function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const memoEddFilesTable = memo(props => {
  const statusLabels = {
    imported: 'Imported',
    not_imported: 'Not Imported',
  };
  const { handleItemChecked, filesRetrieved } = props;
  const filesToDisplay = [...filesRetrieved];

  return (
    <>
      <table className="w-full mt-8 border-separate border-spacing-y-2 table-fixed">
        <thead>
          <tr className="rounded-sm shadow-lg bg-white shadow-[20px_20px_60px_#bcbcbc,-20px_-20px_60px_#ffffff]">
            <th className="w-1/6 p-2 align-middle text-start font-semibold 2xl:w-[35%]">
              <div className="flex items-center gap-2">
                <ToolTip toolText="Toggle Filter" placement="bottom">
                  <button
                    type="button"
                    className="cursor-pointer hover:bg-slate-100 duration-500 flex items-center"
                    onClick={props.handleFilterToggle}
                  >
                    <img
                      className="w-[24px] h-[24px] max-w-[24px] max-h-[24px] hover:scale-x-125 hover:scale-y-125 duration-500"
                      src="/assets/images/icons/filter.svg"
                      alt="Filter"
                      draggable={false}
                    />
                    {props.filterLoading && (
                      <Spinner className="small-spinner" loading={props.filterLoading} />
                    )}
                  </button>
                </ToolTip>
                File Name
              </div>
            </th>
            <th className="w-1/6 p-2 align-middle text-start font-semibold 2xl:w-[10%]">Type</th>
            <th className="w-1/6 p-2 align-middle text-start font-semibold 2xl:w-[14%]">
              Uploaded Date
            </th>
            <th className="w-1/6 p-2 align-middle text-start font-semibold 2xl:w-[14%]">
              Validation Status
            </th>
            <th className="w-1/6 p-2 align-middle text-start font-semibold 2xl:w-[14%]">
              Validation Date
            </th>
            <th className="w-1/6 p-2 align-middle text-start font-semibold 2xl:w-[12%]">
              Import Status
            </th>
            <th className="w-1/6 p-2 align-middle text-start font-semibold invisible 2xl:w-[7%]">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {filesToDisplay.map((el, i) => {
            return (
              <tr
                key={el.id}
                data-test={`edd-files-table-row-${i}`}
                className={classNames(
                  'rounded-sm shadow-lg cursor-pointer duration-500 transition',
                  el.selected ? 'bg-[#ff5f0036]' : 'bg-white',
                )}
                onMouseDown={e => {
                  if (e.shiftKey) {
                    e.preventDefault();
                  }
                }}
                onClick={e => handleItemChecked(e, i)}
              >
                <td className="w-1/6 p-2 align-middle overflow-hidden 2xl:w-[35%] min-w-0">
                  <div className="w-24 2xl:w-full flex items-center justify-start min-w-0">
                    <ToolTip
                      className="2xl:w-full"
                      placement="bottom"
                      toolText={insertSpaceAt46th(el.fileName)}
                    >
                      <p
                        className="text-start text-dark-night font-medium text-sm truncate w-24 2xl:w-full"
                        data-test="edd-table-file-name"
                      >
                        {el.fileName}
                      </p>
                    </ToolTip>
                  </div>
                </td>

                <td className="w-1/6 p-2 align-middle 2xl:w-[10%]">
                  <div className="w-24 flex items-center justify-start 2xl:w-[10%]">
                    <CustomFile fileType={el.type} />
                    <p className="text-start font-medium text-dark-night text-sm">{el.type}</p>
                  </div>
                </td>

                <td className="w-1/6 p-2 align-middle overflow-hidden 2xl:w-[14%]">
                  <div className="w-36 flex items-center justify-start 2xl:w-[14%]">
                    <ToolTip
                      placement="bottom"
                      toolText={`Uploaded by ${capitalize(el.uploadedBy)}`}
                    >
                      <p className="text-start text-dark-night font-medium text-sm truncate w-36">
                        {el.uploadedDateString}
                      </p>
                    </ToolTip>
                  </div>
                </td>

                <td className="w-1/6 p-2 align-middle 2xl:w-[14%]">
                  <div className="w-32 flex items-center justify-center">
                    {el.validationStatus === 'valid' ? (
                      <i
                        data-test="validation-icon-valid"
                        className="fa fa-check-circle-o !text-green-500 !text-[24px] opacity-90"
                      />
                    ) : (
                      <i
                        data-test="validation-icon-invalid"
                        className="fa fa-times-circle-o !text-red-500 !text-[24px] opacity-90"
                      />
                    )}
                  </div>
                </td>

                <td className="w-1/6 p-2 align-middle overflow-hidden 2xl:w-[14%]">
                  <div className="w-36 flex items-center justify-start 2xl:w-full">
                    <ToolTip
                      placement="bottom"
                      toolText={`validated by ${capitalize(el.validatedBy)}`}
                    >
                      <p className="text-start text-dark-night font-medium text-sm truncate w-36 2xl:w-full">
                        {el.validationDateString}
                      </p>
                    </ToolTip>
                  </div>
                </td>

                <td className="w-1/6 p-2 align-middle overflow-hidden 2xl:w-[12%]">
                  <div className="w-36 flex items-center justify-start 2xl:w-full">
                    <ToolTip placement="bottom" toolText={getImportTooltip(el)}>
                      <p className="text-start text-dark-night font-medium text-sm truncate w-36">
                        {statusLabels[el.importStatus] || 'Import Scheduled'}
                      </p>
                    </ToolTip>
                  </div>
                </td>

                <td className="w-1/6 p-2 align-middle text-sm 2xl:w-[7%]">
                  <div className="flex justify-center">
                    <Link
                      to={`/edd/${filesToDisplay[i].id}`}
                      data-test={`edd-files-table-view-${i}`}
                      className="w-24 h-8 text-center bg-btn-primary-form hover:text-white hover:scale-x-105 transition duration-500 rounded-lg text-white font-medium hover:scale-y-105 font-bold flex items-center justify-center"
                    >
                      View
                    </Link>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <PaginationInfo itemsFound={props.totalNumberOfRows} />
      <PaginationPanel itemsFound={props.totalNumberOfRows} />
    </>
  );
});

export default memoEddFilesTable;

memoEddFilesTable.propTypes = {
  totalNumberOfRows: PropTypes.number,
  handleFilterToggle: PropTypes.func,
  handleItemChecked: PropTypes.func,
  filesRetrieved: PropTypes.array,
  filterLoading: PropTypes.bool,
};
